import { useCallback, useState } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import SaveSuccessDialog from '../components/SaveSuccessDialog';

const DEFAULT_SAVED_MSG =
  'Saved to the server. The live website will update within a few seconds.';

/**
 * Shared close / save feedback for admin editors (add, edit, cancel).
 * @param {{ persistNow?: () => Promise<unknown> }} options
 */
export function useEditorFeedback({ persistNow } = {}) {
  const [unsaved, setUnsaved] = useState(null);
  const [savedMsg, setSavedMsg] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [formDirty, setFormDirty] = useState(false);

  const notifySaved = useCallback(
    async (message) => {
      setSaveError(null);
      if (persistNow) {
        try {
          await persistNow();
          setSavedMsg(message || DEFAULT_SAVED_MSG);
        } catch (e) {
          const err = e?.message || 'Could not save to server';
          setSaveError(err);
          setSavedMsg(
            'Changes are in the editor but were not saved to the server. Fix the connection and use Save to server.'
          );
        }
      } else {
        setSavedMsg(message || DEFAULT_SAVED_MSG);
      }
      setFormDirty(false);
    },
    [persistNow]
  );

  const requestClose = useCallback(
    ({ onDiscard, onSave, message, isNew, isDirty }) => {
      const dirty = isDirty ?? formDirty;
      if (!isNew && !dirty) {
        onDiscard();
        return;
      }
      const defaultMessage = isNew
        ? 'This new item is not saved yet. Close without saving? It will be removed.'
        : 'You have unsaved changes. Save before closing, or close without saving?';
      setUnsaved({
        message: message || defaultMessage,
        onDiscard: () => {
          setUnsaved(null);
          setFormDirty(false);
          onDiscard();
        },
        onSave: onSave
          ? async () => {
              const ok = await onSave();
              if (ok === false) return;
              setUnsaved(null);
              setFormDirty(false);
              onDiscard();
              notifySaved();
            }
          : null,
      });
    },
    [formDirty, notifySaved]
  );

  const dialogs = (
    <>
      <UnsavedChangesDialog
        open={!!unsaved}
        message={unsaved?.message}
        onStay={() => setUnsaved(null)}
        onDiscard={unsaved?.onDiscard}
        onSave={unsaved?.onSave}
      />
      <SaveSuccessDialog
        open={!!savedMsg}
        message={savedMsg}
        error={saveError}
        onClose={() => {
          setSavedMsg(null);
          setSaveError(null);
        }}
      />
    </>
  );

  return {
    formDirty,
    setFormDirty,
    requestClose,
    notifySaved,
    dialogs,
  };
}
